import * as React from 'react'
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react'

import {
  useRegistrarCalls, useRegistrarProviders,
  type RegistrarCall, type RegistrarCallFilters,
} from '@/lib/queries'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Label } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { cn, formatDateTime, formatNumber } from '@/lib/utils'

const OPERATION_LABEL: Record<string, string> = {
  check: '查询可注册',
  register: '注册下单',
  unknown: '未标注',
}

const WINDOWS: Array<{ value: string; label: string }> = [
  { value: '1h', label: '最近 1 小时' },
  { value: '24h', label: '最近 24 小时' },
  { value: '168h', label: '最近 7 天' },
  { value: '720h', label: '最近 30 天' },
]

/**
 * 注册商接口调用日志。
 *
 * 这些调用是系统里唯一会花钱、也是唯一对手方在系统之外的部分。下单失败时要问的
 * 永远是同一句话:我们到底发了什么、对方到底回了什么。以前这个答案只存在于已经
 * 滚过去的日志行里,现在落库可查。
 */
export default function RegistrarCallsPage() {
  const [provider, setProvider] = React.useState('')
  const [operation, setOperation] = React.useState('')
  const [domain, setDomain] = React.useState('')
  const [domainQuery, setDomainQuery] = React.useState('')
  const [failuresOnly, setFailuresOnly] = React.useState(false)
  const [window, setWindow] = React.useState('24h')
  const [live, setLive] = React.useState(true)

  const filters: RegistrarCallFilters = {
    provider: provider || undefined,
    operation: operation || undefined,
    domain: domainQuery || undefined,
    failures: failuresOnly || undefined,
    window,
    limit: 200,
  }
  const { data, isLoading, error, refetch, isFetching } = useRegistrarCalls(filters, live)
  const { data: providerData } = useRegistrarProviders()
  const providers = providerData?.providers ?? []
  const nameOf = (id: string) => providers.find((p) => p.id === id)?.name ?? id

  const calls = data?.calls ?? []
  const stats = data?.stats

  return (
    <div>
      <PageHeader
        title="接口调用日志"
        description="系统向各注册商发出的每一次请求与对方的原始回复。查询串里的密钥已脱敏。"
        actions={
          <Button size="sm" variant="outline" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} aria-hidden /> 刷新
          </Button>
        }
      />

      <div className="space-y-4">
        {stats && (
          <div className="grid gap-3 sm:grid-cols-4">
            <StatCard label="调用总数" value={formatNumber(stats.total)} hint={`窗口：${data?.window ?? window}`} />
            <StatCard
              label="失败"
              value={formatNumber(stats.failed)}
              tone={stats.failed > 0 ? 'danger' : undefined}
              hint={stats.total > 0 ? `${((stats.failed / stats.total) * 100).toFixed(1)}%` : '—'}
            />
            <StatCard label="P95 耗时" value={`${formatNumber(stats.p95_ms)} ms`} hint="慢的那一成有多慢" />
            <StatCard
              label="按服务商"
              value={Object.keys(stats.by_provider).length ? '' : '—'}
              hint=""
              body={
                <div className="flex flex-wrap gap-1">
                  {Object.entries(stats.by_provider).map(([id, n]) => (
                    <Badge key={id} variant="muted">{nameOf(id)} {formatNumber(n)}</Badge>
                  ))}
                </div>
              }
            />
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
          <FilterField label="服务商">
            <Select value={provider || 'all'} onValueChange={(v) => setProvider(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label="接口">
            <Select value={operation || 'all'} onValueChange={(v) => setOperation(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="check">查询可注册</SelectItem>
                <SelectItem value="register">注册下单</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label="时间窗口">
            <Select value={window} onValueChange={setWindow}>
              <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label="域名">
            <Input
              className="h-9 w-52" value={domain} placeholder="精确匹配，回车查询"
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setDomainQuery(domain.trim().toLowerCase()) }}
              onBlur={() => setDomainQuery(domain.trim().toLowerCase())}
            />
          </FilterField>

          <label className="flex h-9 items-center gap-2 text-xs">
            <Checkbox checked={failuresOnly} onCheckedChange={(v) => setFailuresOnly(v === true)} />
            只看失败
          </label>
          <label className="flex h-9 items-center gap-2 text-xs">
            <Checkbox checked={live} onCheckedChange={(v) => setLive(v === true)} />
            自动刷新（5 秒）
          </label>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : calls.length === 0 ? (
          <EmptyState
            title="这个窗口里没有调用记录"
            description="换个时间窗口，或者去「注册商接口」页用测试台发一次请求。"
            icon={Activity}
          />
        ) : (
          <CallTable calls={calls} nameOf={nameOf} />
        )}
      </div>
    </div>
  )
}

function StatCard({
  label, value, hint, tone, body,
}: { label: string; value: string; hint?: string; tone?: 'danger'; body?: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      {body ?? (
        <p className={cn('mt-1 text-xl font-semibold tabular-nums', tone === 'danger' && 'text-destructive')}>
          {value}
        </p>
      )}
      {hint && <p className="mt-0.5 text-[0.7rem] text-muted-foreground">{hint}</p>}
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  )
}

function CallTable({
  calls, nameOf,
}: { calls: RegistrarCall[]; nameOf: (id: string) => string }) {
  const [open, setOpen] = React.useState<number | null>(null)

  return (
    <div className="scroll-x rounded-lg border">
      <table className="w-full min-w-[900px] text-sm">
        <caption className="sr-only">注册商接口调用日志，共 {calls.length} 条</caption>
        <thead className="border-b bg-muted/40">
          <tr>
            {['时间', '服务商', '接口', '域名', '结果', '耗时', '发起方', ''].map((h) => (
              <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calls.map((c) => (
            <React.Fragment key={c.id}>
              <tr
                className={cn('border-b last:border-0 hover:bg-muted/40', !c.ok && 'bg-destructive/5')}
              >
                <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-muted-foreground">
                  {formatDateTime(c.created_at)}
                </td>
                <td className="px-3 py-2 text-xs font-medium">{nameOf(c.provider)}</td>
                <td className="px-3 py-2">
                  <Badge variant={c.operation === 'register' ? 'warning' : 'secondary'}>
                    {OPERATION_LABEL[c.operation] ?? c.operation}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{c.domain || '—'}</td>
                <td className="px-3 py-2">
                  {c.ok ? (
                    <Badge variant="success"><CheckCircle2 className="h-3 w-3" aria-hidden />成功</Badge>
                  ) : (
                    <Tooltip content={c.error || `HTTP ${c.http_status}`}>
                      <Badge variant="danger"><XCircle className="h-3 w-3" aria-hidden />失败</Badge>
                    </Tooltip>
                  )}
                  {c.http_status > 0 && (
                    <span className="ml-1 text-[0.7rem] tabular-nums text-muted-foreground">{c.http_status}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{c.duration_ms} ms</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {c.actor === 'worker'
                    ? <Badge variant="muted">后台任务</Badge>
                    : <span className="font-mono text-[0.7rem]">{c.actor?.slice(0, 8) ?? '—'}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setOpen(open === c.id ? null : c.id)}
                  >
                    {open === c.id ? '收起' : '详情'}
                  </Button>
                </td>
              </tr>
              {open === c.id && (
                <tr className="border-b bg-muted/20 last:border-0">
                  <td colSpan={8} className="px-3 py-3">
                    <div className="space-y-2 text-xs">
                      <div>
                        <p className="mb-1 font-medium">请求</p>
                        <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono">
                          {c.method} {c.url}
                        </pre>
                        <p className="mt-1 flex items-start gap-1 text-[0.7rem] text-muted-foreground">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                          查询串里的 API Key、签名等已在入库前替换为 ***，这里看到的不是原始凭据。
                        </p>
                      </div>
                      {c.error && (
                        <div>
                          <p className="mb-1 font-medium text-destructive">错误</p>
                          <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-background p-2">
                            {c.error}
                          </pre>
                        </div>
                      )}
                      <div>
                        <p className="mb-1 font-medium">响应</p>
                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono">
                          {c.response_body || '（无响应体）'}
                        </pre>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
